import type { TradePolicySimulatePolicy, TradePolicySimulateResponse } from "../../../types/tradePolicy";
import type { ExitPlacementSliceRef, SimulateLegs, SimulatedLeg } from "../../../types/managementExplorer";

/** `standalone` = full simulator page; `report` = embedded in report detail. */
export type SimulatorPanelVariant = "standalone" | "report";

export const MAX_COMPARISON_RUNS = 16;

/** Max length for a comparison run title in the simulator results UI (input width caps to this). */
export const MAX_COMPARISON_RUN_TITLE_LENGTH = 64;

export type ComparisonTitleParseMode = "literal" | "legacy";

/** Max length for a run label when combining the mandatory next-run title with a pick name (Option 1, multiple picks). */
export const MAX_SIMULATOR_PICK_RUN_LABEL_LENGTH = 72;

export type ComparisonScopeSnapshot = {
  useCustomRange: boolean;
  timeframe?: string;
  /** Legacy: ISO instants when simulations used start_date/end_date. */
  startDate?: string;
  endDate?: string;
  /** Local calendar window (aligned with statistics / bundles). */
  localStartDate?: string;
  localEndDate?: string;
  timezone?: string;
  symbol: string;
  limit: number;
  /** Always true for new runs — kept/removed ids power trade review. */
  includeTradeIds: boolean;
  candleTimeframe?: "1m" | "5m" | "15m" | "30m" | "1h" | "4h" | "1d";
};

/** Separator between user run title and pick name in tab labels (Option 1). */
export const COMPARISON_RUN_PICK_LABEL_SEP = " — ";

/** Separator used in run tabs between saved title and pick hint (not persisted as backtest title). */
export const COMPARISON_RUN_TAB_HINT_SEP = " · ";

export type ComparisonRun = {
  id: string;
  /** Tab / comparison list label (may include pick suffix after {@link COMPARISON_RUN_PICK_LABEL_SEP}). */
  label: string;
  /** User-entered title from Next run title — shown in the editable simulation name field. */
  savedTitle?: string;
  /** Optional pick name for run tabs only (never written to the saved-title field or backtest title). */
  pickTabHint?: string;
  /** When `literal`, savedTitle is shown/patched verbatim (exit-placement slice labels with ` · `). */
  titleParseMode?: ComparisonTitleParseMode;
  /** Exit Placement: requested replay legs (`simulate_legs` on simulate-exit-placement). */
  simulateLegs?: SimulateLegs;
  /** Exit Placement: legs actually replayed (`simulated_legs` from API). */
  simulatedLegs?: SimulatedLeg[];
  /** Exit Placement: slice context echoed on simulate response. */
  exitPlacementSlices?: ExitPlacementSliceRef[];
  createdAt: number;
  policy: TradePolicySimulatePolicy;
  scope: ComparisonScopeSnapshot;
  result: TradePolicySimulateResponse | null;
  error: string | null;
  status?: "running" | "succeeded" | "failed";
  jobId?: string;
  backtestId?: string;
  /** Strategy to target when saving a policy version (from report link or wizard selection). */
  linkedStrategyId?: string;
  isSavedBacktest?: boolean;
  isSavedStrategyVersion?: boolean;
  /** True when we hit the max poll wall-time; job may still be running server-side. */
  stillRunning?: boolean;
};

/** Stable key for trade-review scope — same value when switching runs with identical simulator scope. */
export function comparisonScopeReviewFingerprint(scope: ComparisonScopeSnapshot): string {
  return [
    scope.symbol.trim(),
    scope.timeframe ?? "",
    scope.localStartDate ?? "",
    scope.localEndDate ?? "",
    scope.startDate ?? "",
    scope.endDate ?? "",
    scope.candleTimeframe ?? "1m",
    scope.timezone ?? "",
  ].join("|");
}

export function formatScopeOneLine(scope: ComparisonScopeSnapshot): string {
  const parts: string[] = [];
  if (scope.localStartDate && scope.localEndDate) {
    parts.push(
      `${scope.localStartDate} → ${scope.localEndDate}${scope.timezone ? ` (${scope.timezone})` : ""}`
    );
  } else if (scope.useCustomRange && scope.startDate && scope.endDate) {
    parts.push(`${scope.startDate.slice(0, 10)} → ${scope.endDate.slice(0, 10)}`);
  } else if (scope.timeframe) {
    parts.push(scope.timeframe);
  } else {
    parts.push("window");
  }
  if (scope.symbol.trim()) parts.push(scope.symbol.trim());
  if (scope.candleTimeframe && scope.candleTimeframe !== "1m") parts.push(`candles ${scope.candleTimeframe}`);
  return parts.join(" · ");
}

export function normalizeComparisonRunSavedTitle(raw: string): string {
  return raw.trim().slice(0, MAX_COMPARISON_RUN_TITLE_LENGTH);
}

const TITLE_PICK_SEPARATORS = [COMPARISON_RUN_PICK_LABEL_SEP, COMPARISON_RUN_TAB_HINT_SEP] as const;

/** Legacy / BE titles may use ASCII or Unicode middle dots with variable spacing. */
const TITLE_PICK_SPLIT_RE = /\s+(?:·|\u00B7|•)\s+|\s+—\s+/;

/** Split a stored/display title into user title vs optional pick tab hint (legacy titles may use either separator). */
export function parseComparisonRunTitleFromRaw(raw: string): { savedTitle: string; pickTabHint?: string } {
  const trimmed = raw.trim();
  if (!trimmed) return { savedTitle: "" };
  for (const sep of TITLE_PICK_SEPARATORS) {
    const idx = trimmed.indexOf(sep);
    if (idx > 0) {
      const savedTitle = normalizeComparisonRunSavedTitle(trimmed.slice(0, idx));
      const pickTabHint = trimmed.slice(idx + sep.length).trim() || undefined;
      return { savedTitle, pickTabHint };
    }
  }
  const flex = trimmed.match(TITLE_PICK_SPLIT_RE);
  if (flex && flex.index != null && flex.index > 0) {
    const savedTitle = normalizeComparisonRunSavedTitle(trimmed.slice(0, flex.index));
    const pickTabHint = trimmed.slice(flex.index + flex[0].length).trim() || undefined;
    return { savedTitle, pickTabHint };
  }
  return { savedTitle: normalizeComparisonRunSavedTitle(trimmed) };
}

/** Persisted backtest / exit-placement titles: keep full string when ` · ` is slice label, not pick suffix. */
export function comparisonRunTitlesFromPersistedTitle(rawTitle: string): {
  savedTitle: string;
  label: string;
  titleParseMode?: ComparisonTitleParseMode;
} {
  const trimmed = rawTitle.trim();
  if (!trimmed) return { savedTitle: "", label: "" };
  const parsed = parseComparisonRunTitleFromRaw(trimmed);
  const splitOnPickLabelSep = trimmed.includes(COMPARISON_RUN_PICK_LABEL_SEP);
  if (parsed.pickTabHint && !splitOnPickLabelSep) {
    const literal = normalizeComparisonRunSavedTitle(trimmed);
    return { savedTitle: literal, label: literal, titleParseMode: "literal" };
  }
  return { savedTitle: parsed.savedTitle, label: parsed.savedTitle };
}

/** Resolve saved title + pick hint from run fields (handles legacy combined labels). */
export function resolveComparisonRunTitles(
  run: Pick<ComparisonRun, "savedTitle" | "label" | "pickTabHint" | "titleParseMode">
): { savedTitle: string; pickTabHint?: string } {
  if (run.titleParseMode === "literal") {
    const literal = normalizeComparisonRunSavedTitle(
      run.savedTitle?.trim() || run.label?.trim() || ""
    );
    return { savedTitle: literal };
  }
  const explicitHint = run.pickTabHint?.trim();
  if (explicitHint) {
    const bases = [run.savedTitle, run.label]
      .map((s) => (s?.trim() ? parseComparisonRunTitleFromRaw(s.trim()).savedTitle : ""))
      .filter((s) => s.length > 0);
    const savedTitle = bases.length
      ? bases.reduce((shortest, next) => (next.length < shortest.length ? next : shortest))
      : "";
    return { savedTitle: normalizeComparisonRunSavedTitle(savedTitle), pickTabHint: explicitHint };
  }
  const raw = run.savedTitle?.trim() || run.label?.trim() || "";
  return parseComparisonRunTitleFromRaw(raw);
}

/** Title shown in the simulation name input — always the user title, never pick metadata. */
export function getComparisonRunSavedTitle(
  run: Pick<ComparisonRun, "savedTitle" | "label" | "pickTabHint" | "titleParseMode">
): string {
  return resolveComparisonRunTitles(run).savedTitle;
}

/** Run tab text — same as the saved simulation name (no pick suffix). */
export function getComparisonRunTabLabel(
  run: Pick<ComparisonRun, "savedTitle" | "label" | "pickTabHint" | "titleParseMode">
): string {
  return getComparisonRunSavedTitle(run);
}

/** Canonicalize label/savedTitle after load or save (strips legacy combined titles and pick suffixes). */
export function normalizeComparisonRunTitleFields(run: ComparisonRun): ComparisonRun {
  if (run.titleParseMode === "literal") {
    const literal = normalizeComparisonRunSavedTitle(
      run.savedTitle?.trim() || run.label?.trim() || ""
    );
    const { pickTabHint: _hint, ...rest } = run;
    return {
      ...rest,
      label: literal,
      savedTitle: literal,
      titleParseMode: "literal",
    };
  }
  const resolved = resolveComparisonRunTitles(run);
  const { pickTabHint: _hint, ...rest } = run;
  return {
    ...rest,
    label: resolved.savedTitle,
    savedTitle: resolved.savedTitle,
  };
}

/**
 * Merge API-hydrated saved runs with in-session state.
 * - Keeps unsaved session runs (no backtestId) that are still only local.
 * - Drops saved runs that are no longer in the hydrated list (deleted / unlinked from report).
 * - Prefers hydrated titles so renames from another surface (e.g. workspace node) win over stale session labels.
 * - When hydrate races ahead of stamping `backtestId` on a just-saved session run, absorb that
 *   session row into the hydrated backtest (same saved title) so the picker does not duplicate.
 */
export function mergeHydratedComparisonRuns(
  existing: ComparisonRun[],
  hydrated: ComparisonRun[]
): ComparisonRun[] {
  const sessionRuns = existing.filter((r) => !r.backtestId?.trim());
  const existingByBacktestId = new Map<string, ComparisonRun>();
  for (const r of existing) {
    const id = r.backtestId?.trim();
    if (id) existingByBacktestId.set(id, r);
  }

  const byBacktestId = new Map<string, ComparisonRun>();
  const absorbedSessionIds = new Set<string>();

  for (const r of hydrated) {
    const id = r.backtestId?.trim();
    if (!id) continue;
    const prev = existingByBacktestId.get(id);
    if (prev) {
      const hydratedTitle = getComparisonRunSavedTitle(r);
      const prevTitle = getComparisonRunSavedTitle(prev);
      const savedTitle = hydratedTitle || prevTitle;
      let titleParseMode: ComparisonTitleParseMode | undefined;
      if (hydratedTitle) {
        titleParseMode = r.titleParseMode === "literal" ? "literal" : undefined;
      } else if (prev.titleParseMode === "literal" || r.titleParseMode === "literal") {
        titleParseMode = "literal";
      }
      byBacktestId.set(id, {
        ...r,
        id: prev.id,
        savedTitle,
        label: savedTitle,
        ...(titleParseMode ? { titleParseMode } : {}),
        pickTabHint: prev.pickTabHint ?? r.pickTabHint,
        jobId: prev.jobId ?? r.jobId,
        linkedStrategyId: prev.linkedStrategyId ?? r.linkedStrategyId,
      });
      continue;
    }

    // Hydrate arrived before the session run was stamped with backtestId — same title = same run.
    const hydratedTitle = getComparisonRunSavedTitle(r);
    const sessionMatch = sessionRuns.find((s) => {
      if (absorbedSessionIds.has(s.id)) return false;
      if (s.status !== "succeeded" && s.status !== "running") return false;
      return getComparisonRunSavedTitle(s) === hydratedTitle;
    });
    if (sessionMatch) {
      absorbedSessionIds.add(sessionMatch.id);
      byBacktestId.set(id, {
        ...r,
        id: sessionMatch.id,
        savedTitle: hydratedTitle || getComparisonRunSavedTitle(sessionMatch),
        label: hydratedTitle || getComparisonRunSavedTitle(sessionMatch),
        ...(r.titleParseMode === "literal" || sessionMatch.titleParseMode === "literal"
          ? { titleParseMode: "literal" as const }
          : {}),
        pickTabHint: sessionMatch.pickTabHint ?? r.pickTabHint,
        jobId: sessionMatch.jobId ?? r.jobId,
        linkedStrategyId: sessionMatch.linkedStrategyId ?? r.linkedStrategyId,
        simulateLegs: sessionMatch.simulateLegs ?? r.simulateLegs,
        simulatedLegs: sessionMatch.simulatedLegs ?? r.simulatedLegs,
        exitPlacementSlices: sessionMatch.exitPlacementSlices ?? r.exitPlacementSlices,
      });
      continue;
    }

    byBacktestId.set(id, r);
  }

  const remainingSession = sessionRuns.filter((s) => !absorbedSessionIds.has(s.id));
  const merged = [...remainingSession, ...Array.from(byBacktestId.values())];
  merged.sort((a, b) => b.createdAt - a.createdAt);
  return merged.slice(0, MAX_COMPARISON_RUNS);
}

function pickSuffixFromLegacyComparisonLabel(label: string): string | null {
  return parseComparisonRunTitleFromRaw(label).pickTabHint ?? null;
}

/** @deprecated Pick suffix is no longer shown in run labels. */
export function pickRunComparisonLabelFromParts(baseTitle: string, _pickLabel: string): string {
  return normalizeComparisonRunSavedTitle(baseTitle);
}

/** @deprecated Renames update `savedTitle` and `label` directly. */
export function comparisonRunLabelAfterSavedTitleRename(savedTitle: string, _previousLabel: string): string {
  return normalizeComparisonRunSavedTitle(savedTitle);
}
